import React from "react";
import { supabase } from "@/integrations/supabase/client";
import { objectiveLabel } from "../hooks/useAiBundles";

type Bundle = {
  id: string;
  objective: string;
  title: string;
  description: string | null;
  items: Array<{ product_id: number; qty: number }>;
  discount_pct: number | null;
  source: "cron" | "personal";
  is_active: boolean;
  user_id: string | null;
  created_at: string;
};

type ProductPreview = {
  id: string;
  name: string;
  brand: string | null;
  price: number | null;
  image_url: string | null;
  is_active: boolean | null;
  stock_quantity: number | null;
};

export function AdminAiBundles({ lang, toast }: { lang: "es" | "en"; toast?: (m: string) => void }) {
  const [bundles, setBundles] = React.useState<Bundle[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [filter, setFilter] = React.useState<"all" | "cron" | "personal" | "pending">("all");
  const [productMap, setProductMap] = React.useState<Record<string, ProductPreview>>({});

  const t = (es: string, en: string) => (lang === "es" ? es : en);

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ai_bundles")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (!error && data) setBundles(data as any);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Fetch a lightweight preview for every product referenced by bundles.
  React.useEffect(() => {
    const ids = Array.from(
      new Set(
        bundles.flatMap((b) =>
          Array.isArray(b.items) ? b.items.map((i) => String(i.product_id)) : []
        )
      )
    ).filter((id) => id && !productMap[id]);
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id,name,brand,price,image_url,is_active,stock_quantity")
        .in("id", ids);
      if (cancelled || error || !data) return;
      setProductMap((prev) => {
        const next = { ...prev };
        for (const p of data as any[]) next[String(p.id)] = p as ProductPreview;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [bundles, productMap]);

  const filtered = bundles.filter((b) => {
    if (filter === "pending") return !b.is_active;
    if (filter === "cron" || filter === "personal") return b.source === filter;
    return true;
  });

  const generateBatch = async () => {
    setGenerating(true);
    try {
      const { error } = await supabase.functions.invoke("generate-ai-bundles", { body: {} });
      if (error) throw error;
      toast?.(t("✅ Bundles generados", "✅ Bundles generated"));
      await load();
    } catch (e: any) {
      toast?.(t("❌ Error: ", "❌ Error: ") + (e.message || e));
    } finally {
      setGenerating(false);
    }
  };

  const updateBundle = async (id: string, patch: Partial<Bundle>) => {
    const { error } = await supabase.from("ai_bundles").update(patch as any).eq("id", id);
    if (error) { toast?.("❌ " + error.message); return; }
    setBundles((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } as Bundle : b)));
  };

  const approve = (b: Bundle) =>
    updateBundle(b.id, { is_active: true } as any).then(() =>
      toast?.(t("✅ Aprobado y visible", "✅ Approved & visible"))
    );

  const remove = async (id: string) => {
    if (!confirm(t("¿Eliminar bundle?", "Delete bundle?"))) return;
    const { error } = await supabase.from("ai_bundles").delete().eq("id", id);
    if (error) { toast?.("❌ " + error.message); return; }
    setBundles((prev) => prev.filter((b) => b.id !== id));
  };

  // Group cron bundles by objective for per-objective bulk toggle
  const objectiveGroups = React.useMemo(() => {
    const m = new Map<string, { total: number; active: number; ids: string[] }>();
    for (const b of bundles) {
      if (b.source !== "cron") continue;
      const g = m.get(b.objective) ?? { total: 0, active: 0, ids: [] };
      g.total += 1;
      if (b.is_active) g.active += 1;
      g.ids.push(b.id);
      m.set(b.objective, g);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [bundles]);

  const toggleObjective = async (objective: string, activate: boolean) => {
    const ids = bundles.filter((b) => b.source === "cron" && b.objective === objective).map((b) => b.id);
    if (ids.length === 0) return;
    const { error } = await supabase.from("ai_bundles").update({ is_active: activate } as any).in("id", ids);
    if (error) { toast?.("❌ " + error.message); return; }
    setBundles((prev) => prev.map((b) => (ids.includes(b.id) ? { ...b, is_active: activate } : b)));
    toast?.(
      activate
        ? t(`✅ Activados: ${objectiveLabel(objective, lang)}`, `✅ Activated: ${objectiveLabel(objective, "en")}`)
        : t(`⏸️ Desactivados: ${objectiveLabel(objective, lang)}`, `⏸️ Deactivated: ${objectiveLabel(objective, "en")}`)
    );
  };

  return (
    <div data-testid="admin-ai-bundles">
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>{t("Bundles AI", "AI Bundles")}</h3>
        <button className="btn primary" onClick={generateBatch} disabled={generating}>
          {generating ? t("Generando…", "Generating…") : t("⚡ Generar batch global", "⚡ Generate global batch")}
        </button>
        <button className="btn" onClick={load} disabled={loading}>
          {t("↻ Recargar", "↻ Reload")}
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {(["all", "pending", "cron", "personal"] as const).map((f) => (
            <button key={f} className={`atab ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)}>
              {f === "all" ? t("Todos", "All")
                : f === "pending" ? t("Pendientes", "Pending")
                : f === "cron" ? t("Globales", "Global")
                : t("Personales", "Personal")}
            </button>
          ))}
        </div>
      </div>

      <p style={{ color: "var(--tx3)", fontSize: 13 }}>
        {t(
          "Los bundles globales se generan inactivos. Fija el descuento (a discreción admin) y actívalos para que aparezcan en el catálogo.",
          "Global bundles start inactive. Set discount (admin discretion) and activate to publish."
        )}
      </p>

      {objectiveGroups.length > 0 && (
        <div
          data-testid="objective-toggles"
          style={{
            border: "1px solid var(--bd2)",
            borderRadius: 10,
            padding: 12,
            marginBottom: 16,
            background: "var(--bg2)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
            {t("Activar / desactivar por objetivo", "Activate / deactivate by objective")}
          </div>
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
            {objectiveGroups.map(([objective, g]) => {
              const allOn = g.active === g.total && g.total > 0;
              const someOn = g.active > 0 && !allOn;
              return (
                <label
                  key={objective}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    border: "1px solid var(--bd2)",
                    borderRadius: 8,
                    background: "var(--bg1)",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={(el) => { if (el) el.indeterminate = someOn; }}
                    onChange={(e) => toggleObjective(objective, e.target.checked)}
                    aria-label={`Toggle ${objective}`}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{objectiveLabel(objective, lang)}</div>
                    <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                      {g.active}/{g.total} {t("activos", "active")}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <div>…</div> : (
        <div style={{ display: "grid", gap: 12 }}>
          {filtered.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "var(--tx3)" }}>
              {t("Sin bundles. Genera uno con el botón ⚡.", "No bundles yet. Generate one with ⚡.")}
            </div>
          )}
          {filtered.map((b) => (
            <div key={b.id} style={{ border: "1px solid var(--bd2)", borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <strong>{b.title}</strong>
                <span className="chip">{b.objective}</span>
                <span className="chip">{b.source}</span>
                <span className={`chip ${b.is_active ? "on" : ""}`}>
                  {b.is_active ? t("Activo", "Active") : t("Pendiente", "Pending")}
                </span>
                <span style={{ marginLeft: "auto", color: "var(--tx3)", fontSize: 12 }}>
                  {new Date(b.created_at).toLocaleDateString()}
                </span>
              </div>
              {b.description && <p style={{ margin: "8px 0", fontSize: 13, color: "var(--tx2)" }}>{b.description}</p>}
              <div style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 8 }}>
                {b.items.length} {t("productos", "products")}
              </div>
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  marginBottom: 8,
                }}
              >
                {b.items.map((it, idx) => {
                  const p = productMap[String(it.product_id)];
                  const missing = !p;
                  const inactive = p && p.is_active === false;
                  const oos = p && (p.stock_quantity ?? 0) <= 0;
                  const warn = missing || inactive || oos;
                  return (
                    <div
                      key={`${b.id}-${it.product_id}-${idx}`}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        padding: 6,
                        border: `1px solid ${warn ? "var(--err, #c33)" : "var(--bd2)"}`,
                        borderRadius: 8,
                        background: "var(--bg1)",
                      }}
                      title={
                        missing
                          ? t("Producto no encontrado en catálogo", "Product not found in catalog")
                          : inactive
                          ? t("Producto inactivo (no se publicará)", "Product inactive (won't publish)")
                          : oos
                          ? t("Sin stock", "Out of stock")
                          : undefined
                      }
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 6,
                          overflow: "hidden",
                          background: "var(--bg2)",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {p?.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            loading="lazy"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <span style={{ fontSize: 18 }}>📦</span>
                        )}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {p?.name ?? t("(producto desconocido)", "(unknown product)")}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                          {p?.brand ? `${p.brand} · ` : ""}×{it.qty}
                          {p?.price != null ? ` · RD$${Number(p.price).toFixed(0)}` : ""}
                        </div>
                        {warn && (
                          <div style={{ fontSize: 10, color: "var(--err, #c33)", fontWeight: 600 }}>
                            {missing
                              ? t("⚠ No encontrado", "⚠ Not found")
                              : inactive
                              ? t("⚠ Inactivo", "⚠ Inactive")
                              : t("⚠ Sin stock", "⚠ Out of stock")}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontSize: 12 }}>{t("Descuento %", "Discount %")}:</label>
                <input
                  type="number" min={0} max={50} step={1}
                  defaultValue={b.discount_pct ?? ""}
                  style={{ width: 80 }}
                  onBlur={(e) => {
                    const v = e.target.value === "" ? null : Number(e.target.value);
                    if (v !== b.discount_pct) updateBundle(b.id, { discount_pct: v } as any);
                  }}
                />
                {!b.is_active && b.source === "cron" && (
                  <button className="btn primary" onClick={() => approve(b)}>
                    {t("✓ Aprobar y publicar", "✓ Approve & publish")}
                  </button>
                )}
                {b.is_active && (
                  <button className="btn" onClick={() => updateBundle(b.id, { is_active: false } as any)}>
                    {t("Desactivar", "Deactivate")}
                  </button>
                )}
                <button className="btn danger" onClick={() => remove(b.id)} style={{ marginLeft: "auto" }}>
                  {t("Eliminar", "Delete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}